import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  COUNTRY_OPTIONS,
  EMPLOYEE_SIZE_OPTIONS,
  ENTITY_TYPE_OPTIONS,
} from "./acceptSupplierInvitationConstants";
import useAcceptSupplierInvitation from "./useAcceptSupplierInvitation";
import {
  AcceptInvitationShell,
  Field,
  InvitationLogo,
  SectionCard,
  fieldsetResetStyle,
} from "./components/AcceptInvitationChrome";
import PercentRowList from "./components/PercentRowList";
import EmployeeCountBlock from "./components/EmployeeCountBlock";
import "./signup.css";
import "./acceptSupplierInvitation.css";

/**
 * Supplier accept-invitation page. All state, fetch, validation, and submit
 * live in `useAcceptSupplierInvitation`; this component is presentation
 * only. The password-visibility toggle is UI-local state.
 */
export default function AcceptSupplierInvitation() {
  const {
    form,
    setField,
    industries,
    industriesLoading,
    subIndustries,
    selectedIndustryId,
    selectedSubIndustryId,
    handleIndustryChange,
    handleSubIndustryChange,
    submitted,
    validationErrors,
    submit,
    submitting,
    submitError,
    loadingInvite,
    loadError,
    inviteValid,
    canSubmit,
    canUpdate,
    contactOnlyMode,
    contactEditable,
    profileReadOnly,
    invitationMeta,
    goToLogin,
  } = useAcceptSupplierInvitation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const needsCin =
    form.entity_type === "LIMITED" || form.entity_type === "PRIVATE_LIMITED";
  const needsGstin = form.entity_type === "PROPRIETORSHIP";
  const needsPan = form.entity_type === "OTHER";

  if (loadingInvite) {
    return (
      <AcceptInvitationShell>
        <InvitationLogo />
        <div className="accept-supplier-invite__status">
          <Spinner animation="border" variant="primary" />
          <p className="faster_oval mt-3 mb-0">Validating invitation…</p>
        </div>
      </AcceptInvitationShell>
    );
  }

  if (loadError || !inviteValid) {
    return (
      <AcceptInvitationShell>
        <InvitationLogo />
        <div className="accept-supplier-invite__status">
          <div
            className="accept-supplier-invite__status-icon accept-supplier-invite__status-icon--error"
            aria-hidden
          >
            !
          </div>
          <h4 className="Account">Invitation unavailable</h4>
          <p className="accept-supplier-invite__error-msg">
            {loadError || "Invalid invitation link."}
          </p>
          <button
            type="button"
            className="page_save page_width"
            onClick={goToLogin}
          >
            Go to login
          </button>
        </div>
      </AcceptInvitationShell>
    );
  }

  const passwordAdornment = (visible, toggle, ariaLabel) => (
    <button
      type="button"
      className="btn btn-link btn-sm"
      style={{
        position: "absolute",
        right: 8,
        top: "50%",
        transform: "translateY(-50%)",
        padding: 0,
        fontSize: 12,
      }}
      onClick={toggle}
      aria-label={ariaLabel}
    >
      {visible ? "Hide" : "Show"}
    </button>
  );

  return (
    <AcceptInvitationShell wide>
      <InvitationLogo />
      <div className="text_sing mb-4 text-center">
        <h4 className="Account">
          {contactOnlyMode
            ? "Set up your account"
            : canUpdate
              ? "Accept supplier invitation"
              : "Supplier profile"}
        </h4>
        <p className="faster_oval mb-0">
          {contactOnlyMode
            ? "Your company is already registered. Enter your contact details and create a password to finish setting up your account."
            : canUpdate
              ? "Complete your company profile to register as a supplier."
              : "Your supplier profile is shown below (read-only)."}
        </p>
      </div>

      <form
        className="accept-supplier-invite__form"
        onSubmit={submit}
        noValidate
      >
        {submitError ? (
          <div
            className="alert alert-danger mb-3"
            role="alert"
            style={{ whiteSpace: "pre-line" }}
          >
            {submitError}
          </div>
        ) : null}
        {!canSubmit ? (
          <div className="alert alert-info mb-3" role="status">
            This invitation is linked to an existing supplier record
            {invitationMeta?.companyName
              ? ` (${invitationMeta.companyName})`
              : ""}
            . Updates are not allowed from this link.
          </div>
        ) : null}
        {contactOnlyMode ? (
          <div className="alert alert-info mb-3" role="status">
            Company, business profile, and operations were completed when the
            supplier was first onboarded — they're shown below for reference
            and can't be edited from this invite.
          </div>
        ) : null}

        <SectionCard title="Contact">
          <div className="row">
            <div className="col-md-6">
              <Field
                label="First name"
                required
                error={submitted ? validationErrors.first_name : ""}
              >
                <input
                  className="form-control name_nf"
                  value={form.first_name}
                  disabled={!contactEditable}
                  readOnly={!contactEditable}
                  onChange={(e) => setField("first_name", e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field
                label="Last name"
                required
                error={submitted ? validationErrors.last_name : ""}
              >
                <input
                  className="form-control name_nf"
                  value={form.last_name}
                  disabled={!contactEditable}
                  readOnly={!contactEditable}
                  onChange={(e) => setField("last_name", e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field
                label="Email"
                required
                error={submitted ? validationErrors.email : ""}
              >
                <input
                  className="form-control name_nf"
                  type="email"
                  value={form.email}
                  disabled
                  readOnly
                  onChange={(e) => setField("email", e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field
                label="Mobile"
                required
                error={submitted ? validationErrors.mobile : ""}
              >
                <input
                  className="form-control name_nf"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.mobile}
                  disabled={!contactEditable}
                  readOnly={!contactEditable}
                  onChange={(e) =>
                    setField(
                      "mobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "Escape",
                        "Enter",
                        "ArrowLeft",
                        "ArrowRight",
                        "Home",
                        "End",
                      ].includes(e.key) ||
                      e.ctrlKey ||
                      e.metaKey
                    ) {
                      return;
                    }
                    if (!/^\d$/.test(e.key)) e.preventDefault();
                  }}
                  placeholder="9999999999"
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field
                label="Country"
                required
                error={submitted ? validationErrors.country : ""}
              >
                <select
                  className="form-control name_nf"
                  value={form.country}
                  disabled={!contactEditable}
                  onChange={(e) => setField("country", e.target.value)}
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {canUpdate ? (
              <>
                <div className="col-md-6">
                  <Field
                    label="Password"
                    required
                    error={submitted ? validationErrors.password : ""}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        className="form-control name_nf"
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => setField("password", e.target.value)}
                        placeholder="Create login password"
                        autoComplete="new-password"
                      />
                      {passwordAdornment(
                        showPassword,
                        () => setShowPassword((v) => !v),
                        showPassword ? "Hide password" : "Show password"
                      )}
                    </div>
                  </Field>
                </div>
                <div className="col-md-6">
                  <Field
                    label="Confirm password"
                    required
                    error={submitted ? validationErrors.confirm_password : ""}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        className="form-control name_nf"
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirm_password}
                        onChange={(e) =>
                          setField("confirm_password", e.target.value)
                        }
                        placeholder="Re-enter password"
                        autoComplete="new-password"
                      />
                      {passwordAdornment(
                        showConfirmPassword,
                        () => setShowConfirmPassword((v) => !v),
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      )}
                    </div>
                  </Field>
                </div>
              </>
            ) : null}
            {contactOnlyMode ? (
              <>
                <div className="col-md-6">
                  <Field
                    label="Create password"
                    required
                    error={submitted ? validationErrors.password : ""}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        className="form-control name_nf"
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        disabled={!contactEditable}
                        onChange={(e) => setField("password", e.target.value)}
                        placeholder="Choose a password"
                        autoComplete="new-password"
                      />
                      {passwordAdornment(
                        showPassword,
                        () => setShowPassword((v) => !v),
                        showPassword ? "Hide password" : "Show password"
                      )}
                    </div>
                  </Field>
                </div>
                <div className="col-md-6">
                  <Field
                    label="Confirm password"
                    required
                    error={submitted ? validationErrors.confirm_password : ""}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        className="form-control name_nf"
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirm_password}
                        disabled={!contactEditable}
                        onChange={(e) =>
                          setField("confirm_password", e.target.value)
                        }
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                      />
                      {passwordAdornment(
                        showConfirmPassword,
                        () => setShowConfirmPassword((v) => !v),
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      )}
                    </div>
                  </Field>
                </div>
              </>
            ) : null}
          </div>
        </SectionCard>

        <fieldset disabled={profileReadOnly} style={fieldsetResetStyle}>
          <SectionCard title="Company">
            <div className="row">
              <div className="col-md-6">
                <Field
                  label="Company name"
                  required
                  error={submitted ? validationErrors.company_name : ""}
                >
                  <input
                    className="form-control name_nf"
                    value={form.company_name}
                    disabled
                    readOnly
                    onChange={(e) => setField("company_name", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field
                  label="Entity type"
                  required
                  error={submitted ? validationErrors.entity_type : ""}
                >
                  <select
                    className="form-control name_nf"
                    value={form.entity_type}
                    onChange={(e) => setField("entity_type", e.target.value)}
                  >
                    {ENTITY_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {needsCin ? (
                <div className="col-md-6">
                  <Field
                    label="CIN"
                    required
                    error={submitted ? validationErrors.cin : ""}
                  >
                    <input
                      className="form-control name_nf"
                      value={form.cin}
                      onChange={(e) => setField("cin", e.target.value)}
                      placeholder="U12345MH2020PTC123456"
                    />
                  </Field>
                </div>
              ) : null}
              {needsGstin ? (
                <div className="col-md-6">
                  <Field
                    label="GSTIN"
                    required
                    error={submitted ? validationErrors.gstin : ""}
                  >
                    <input
                      className="form-control name_nf"
                      value={form.gstin}
                      onChange={(e) => setField("gstin", e.target.value)}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </Field>
                </div>
              ) : null}
              {needsPan ? (
                <div className="col-md-6">
                  <Field
                    label="PAN"
                    required
                    error={submitted ? validationErrors.pan : ""}
                  >
                    <input
                      className="form-control name_nf"
                      value={form.pan}
                      onChange={(e) => setField("pan", e.target.value)}
                      placeholder="ABCDE1234F"
                    />
                  </Field>
                </div>
              ) : null}
              <div className="col-md-6">
                <Field
                  label="Employee size"
                  required
                  error={
                    submitted ? validationErrors.employee_size_category : ""
                  }
                >
                  <select
                    className="form-control name_nf"
                    value={form.employee_size_category}
                    onChange={(e) =>
                      setField("employee_size_category", e.target.value)
                    }
                  >
                    {EMPLOYEE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-md-12">
                <Field
                  label="Address line 1"
                  required
                  error={submitted ? validationErrors.address1 : ""}
                >
                  <input
                    className="form-control name_nf"
                    value={form.address1}
                    onChange={(e) => setField("address1", e.target.value)}
                    placeholder="Street, building, area"
                  />
                </Field>
              </div>
              <div className="col-md-12">
                <Field label="Address line 2">
                  <input
                    className="form-control name_nf"
                    value={form.address2}
                    onChange={(e) => setField("address2", e.target.value)}
                    placeholder="Suite, floor (optional)"
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field
                  label="City"
                  required
                  error={submitted ? validationErrors.city : ""}
                >
                  <input
                    className="form-control name_nf"
                    value={form.city}
                    onChange={(e) => setField("city", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field
                  label="State"
                  required
                  error={submitted ? validationErrors.state : ""}
                >
                  <input
                    className="form-control name_nf"
                    value={form.state}
                    onChange={(e) => setField("state", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field
                  label="Zip code"
                  required
                  error={submitted ? validationErrors.zip_code : ""}
                >
                  <input
                    className="form-control name_nf"
                    value={form.zip_code}
                    onChange={(e) => setField("zip_code", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field
                  label="Industry"
                  required
                  error={submitted ? validationErrors.industry : ""}
                >
                  <select
                    className="form-control name_nf"
                    value={selectedIndustryId}
                    onChange={(e) => handleIndustryChange(e.target.value)}
                    disabled={industriesLoading || profileReadOnly}
                  >
                    <option value="">
                      {industriesLoading
                        ? "Loading industries…"
                        : "Select industry"}
                    </option>
                    {industries.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-md-6">
                <Field
                  label="Sub-industry"
                  required
                  error={submitted ? validationErrors.sub_industry : ""}
                >
                  <select
                    className="form-control name_nf"
                    value={selectedSubIndustryId}
                    onChange={(e) => handleSubIndustryChange(e.target.value)}
                    disabled={
                      industriesLoading ||
                      !selectedIndustryId ||
                      profileReadOnly
                    }
                  >
                    <option value="">
                      {!selectedIndustryId
                        ? "Select industry first"
                        : "Select sub-industry"}
                    </option>
                    {subIndustries.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Business profile">
            <PercentRowList
              label="Business activities (%)"
              rows={form.business_activities}
              submitted={submitted}
              required
              readOnly={profileReadOnly}
              onChange={(rows) => setField("business_activities", rows)}
            />
            {submitted && validationErrors.business_activities ? (
              <div className="help-block mb-2">
                {validationErrors.business_activities}
              </div>
            ) : null}
            <PercentRowList
              label="Products or services (%)"
              rows={form.products_or_services}
              submitted={submitted}
              required
              readOnly={profileReadOnly}
              onChange={(rows) => setField("products_or_services", rows)}
            />
            {submitted && validationErrors.products_or_services ? (
              <div className="help-block mb-2">
                {validationErrors.products_or_services}
              </div>
            ) : null}
          </SectionCard>

          <SectionCard title="Operations">
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="st_name d-block mb-2">
                  Manufacturing plants *
                </label>
                <div className="row">
                  <div className="col-6">
                    <input
                      className="form-control name_nf"
                      type="number"
                      min="0"
                      required
                      placeholder="National"
                      value={form.manufacturing_plants.national}
                      onChange={(e) =>
                        setField("manufacturing_plants", {
                          ...form.manufacturing_plants,
                          national: e.target.value,
                        })
                      }
                    />
                    {submitted &&
                    validationErrors.manufacturing_plants_national ? (
                      <div className="help-block">
                        {validationErrors.manufacturing_plants_national}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6">
                    <input
                      className="form-control name_nf"
                      type="number"
                      min="0"
                      required
                      placeholder="International"
                      value={form.manufacturing_plants.international}
                      onChange={(e) =>
                        setField("manufacturing_plants", {
                          ...form.manufacturing_plants,
                          international: e.target.value,
                        })
                      }
                    />
                    {submitted &&
                    validationErrors.manufacturing_plants_international ? (
                      <div className="help-block">
                        {validationErrors.manufacturing_plants_international}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <label className="st_name d-block mb-2">Offices *</label>
                <div className="row">
                  <div className="col-6">
                    <input
                      className="form-control name_nf"
                      type="number"
                      min="0"
                      required
                      placeholder="National"
                      value={form.offices.national}
                      onChange={(e) =>
                        setField("offices", {
                          ...form.offices,
                          national: e.target.value,
                        })
                      }
                    />
                    {submitted && validationErrors.offices_national ? (
                      <div className="help-block">
                        {validationErrors.offices_national}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-6">
                    <input
                      className="form-control name_nf"
                      type="number"
                      min="0"
                      required
                      placeholder="International"
                      value={form.offices.international}
                      onChange={(e) =>
                        setField("offices", {
                          ...form.offices,
                          international: e.target.value,
                        })
                      }
                    />
                    {submitted && validationErrors.offices_international ? (
                      <div className="help-block">
                        {validationErrors.offices_international}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <EmployeeCountBlock
              label="Permanent employees"
              errorPrefix="permanent_employees"
              value={form.permanent_employees}
              onChange={(v) => setField("permanent_employees", v)}
              submitted={submitted}
              errors={validationErrors}
              readOnly={profileReadOnly}
            />
            <EmployeeCountBlock
              label="Non-permanent employees"
              errorPrefix="non_permanent_employees"
              value={form.non_permanent_employees}
              onChange={(v) => setField("non_permanent_employees", v)}
              submitted={submitted}
              errors={validationErrors}
              readOnly={profileReadOnly}
            />
          </SectionCard>
        </fieldset>

        <div className="text-center accept-supplier-invite__submit">
          {canSubmit ? (
            submitting ? (
              <button
                type="button"
                className="page_save page_width"
                disabled
              >
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting…
              </button>
            ) : (
              <button type="submit" className="page_save page_width">
                {contactOnlyMode
                  ? "Save contact details"
                  : "Submit & accept invitation"}
              </button>
            )
          ) : (
            <button
              type="button"
              className="page_save page_width"
              onClick={goToLogin}
            >
              Go to login
            </button>
          )}
        </div>
      </form>
    </AcceptInvitationShell>
  );
}
